import React, { useState, useRef, useEffect } from "react";
import SubBrainform from "../shared/SubBrainform";
import ProTip from "../shared/ProTip";
import Maybe from "./Maybe";
import ThumbsUpDecoy from "./ThumbsUpDecoy";
import ThumbsDownDecoy from "./ThumbsDownDecoy";
import Keepers from "./Keepers";
import Puppet from "./Puppet";
import { PROTIPS } from "../staticData";
import SVG from "react-inlinesvg";

import {
  StyledPrompt,
  StyledMaybeList,
  StyledMaybeListContainer,
  StyledRightColumn,
  StyledPromptLoaderArea,
  StyledSubBrainform,
  StyledLoader,
} from "./styles";

import { randomNumber } from "./staticData";
import { sendAppEvent } from "../../../Utils";


const StreamingList = ({
  items,
  setItems,
  prompt,
  setPrompt,
  userInteracted,
  keepers,
  update,
  setKeepers,
  closeBrainstorm,
  rejected,
  setRejected,
  setEndTime,
  starters,
  setStarters,
  setProTip,
  currentScreen,
  setCurrentScreen,
  setContext,
  context,
  seenTips,
  showProTip,
  setSeenTips,
  proTip,
  setShowProTip,
  setQueuedItems,
  setHasThumbsedUp,
  hasThumbsedUp,
  setIgnoreItemCount,
}) => {
  const [editing, setEditing] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [decoyContent, setDecoyContent] = useState(null); // null or some text
  const [isThumbsed, setIsThumbsed] = useState(false);
  const [decoyLeft, setDecoyLeft] = useState(null);
  const [decoyTop, setDecoyTop] = useState(null);
  const [keepersListScrolled, setKeepersListScrolled] = useState(false);

  const maybeListRef = useRef(null);

  const [listHeight, setListHeight] = useState(920);


  const handleOpenBrainform = () => {
    setEditing(true);
    handleSeenTip(3);
    sendAppEvent("Brainstorm - Open In-Situ Editor");
  };

  const truncatedPrompt = prompt.length > 40 ? prompt.slice(0, 40).trim() + "..." : prompt;

  const handleSeenTip = (tip) => {
    setSeenTips([...seenTips, tip]);
    setShowProTip(false);
  };

  const handleReload = (e) => {
    if (loading) return false;

    setLoading(true);
    if (!e.metaKey) {
      setRejected([...rejected, ...items]);
      setItems([]);
      setQueuedItems([]);
      setIgnoreItemCount(true);

      setTimeout(() => {
        setLoading(false);
        setIgnoreItemCount(false);
        sendAppEvent("Brainstorm - Reload Maybe List");
      }, 1000);
    } else {
      setIgnoreItemCount(true);
      setQueuedItems([]);
      setItems([]);

      setTimeout(() => {
        setIgnoreItemCount(false);
        setLoading(false);
        sendAppEvent("Brainstorm - Meta Reload Maybe List");
      }, 4000);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (maybeListRef.current) {
        setListHeight(window.innerHeight - maybeListRef.current.getBoundingClientRect().top);
      }
    }, 100);
  }, [window.innerHeight, editing]);

  return (
    <>
      {showProTip && proTip > 2 && (
        <ProTip
          {...PROTIPS[proTip]}
          header="Pro Tip"
          setShowProTip={setShowProTip}
          success={() => handleSeenTip(proTip)}
        />
      )}
      {!editing && (
        <StyledPromptLoaderArea>
          <StyledPrompt>
            {truncatedPrompt}
            <div className="edit-action">
              <SVG
                src="/assets/pencil.svg"
                height="24"
                width="24"
                onClick={handleOpenBrainform}
              />
            </div>
          </StyledPrompt>
          <StyledLoader
            onClick={handleReload}
            $loading={loading}
          >
            <SVG src="/assets/brainstorm/refresh.svg" />
          </StyledLoader>
        </StyledPromptLoaderArea>
      )}
      {editing && (
        <StyledSubBrainform>
          <SubBrainform
            starters={starters}
            setStarters={setStarters}
            userInteracted={userInteracted}
            update={update}
            setShowProTip={setShowProTip}
            setProTip={setProTip}
            currentScreen={currentScreen}
            seenTips={seenTips}
            setPrompt={setPrompt}
            setContext={setContext}
            prompt={prompt}
            context={context}
            showExamples={true}
            setEditing={setEditing}
            isInSitu={true}
            setQueuedItems={setQueuedItems}
            setItems={setItems}
            setKeepers={setKeepers}
            keepers={keepers}
          />
        </StyledSubBrainform>
      )}
      <StyledRightColumn editing={editing}>
        <Puppet />
        <Keepers
          keepersList={keepers}
          setKeepers={setKeepers}
          items={items}
          setItems={setItems}
          closeBrainstorm={closeBrainstorm}
          setCurrentScreen={setCurrentScreen}
          setEndTime={setEndTime}
          setQueuedItems={setQueuedItems}
          starters={starters}
          animating={animating}
          setKeepersListScrolled={setKeepersListScrolled}
          rejected={rejected}
          setRejected={setRejected}
        >
          {decoyContent && isThumbsed === "down" && (
            <ThumbsDownDecoy
              isDecoy={true}
              key={"thumbs_down_decoy"}
              content={decoyContent}
              setItems={setItems}
              items={items}
              rejected={rejected}
              setRejected={setRejected}
              animating={animating}
              setAnimating={setAnimating}
              decoyLeft={decoyLeft}
              decoyTop={decoyTop}
              setDecoyContent={setDecoyContent}
              keepersListScrolled={keepersListScrolled}
            />
          )}

          {decoyContent && isThumbsed === "up" && (
            <ThumbsUpDecoy
              key="thumbs-up_decoy"
              content={decoyContent}
              setKeepers={setKeepers}
              setItems={setItems}
              items={items}
              keepers={keepers}
              animating={animating}
              setAnimating={setAnimating}
              seenTips={seenTips}
              handleSeenTip={handleSeenTip}
              setHasThumbsedUp={setHasThumbsedUp}
              hasThumbsedUp={hasThumbsedUp}
              isThumbsed={isThumbsed}
              decoyLeft={decoyLeft}
              decoyTop={decoyTop}
              setDecoyContent={setDecoyContent}
              keepersListScrolled={keepersListScrolled}
            />
          )}
        </Keepers>
      </StyledRightColumn>
      <StyledMaybeListContainer listHeight={listHeight}>
        <StyledMaybeList
          ref={maybeListRef}
          listHeight={listHeight}
        >
          {items &&
            items.map((item, i) => {
              //use item length to always associate the same rotation
              const random = randomNumber(item.length);
              return (
                <Maybe
                  key={"maybe_" + item}
                  content={item}
                  index={i}
                  rotation={random}
                  setKeepers={setKeepers}
                  setItems={setItems}
                  items={items}
                  keepers={keepers}
                  rejected={rejected}
                  setRejected={setRejected}
                  animating={animating}
                  setAnimating={setAnimating}
                  seenTips={seenTips}
                  handleSeenTip={handleSeenTip}
                  setHasThumbsedUp={setHasThumbsedUp}
                  hasThumbsedUp={hasThumbsedUp}
                  setDecoyContent={setDecoyContent}
                  setIsThumbsed={setIsThumbsed}
                  setDecoyLeft={setDecoyLeft}
                  setDecoyTop={setDecoyTop}
                  listScrolled={maybeListRef.current && maybeListRef.current.scrollTop}
                />
              );
            })}
        </StyledMaybeList>
      </StyledMaybeListContainer>
    </>
  );
};

export default StreamingList;
